import { useEffect, useState } from "react";
import api, { fmtDateTime, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function AmdenUsers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [lockedMap, setLockedMap] = useState({});
  const load = () => api.get("/amden-users").then((r) => setRows(r.data));
  const loadLocked = () => api.get("/admin/locked-logins").then((r) => {
    const m = {}; (r.data.locked || []).forEach((l) => { m[l.email] = l.minutes_remaining; }); setLockedMap(m);
  }).catch(() => {});
  useEffect(() => { load(); loadLocked(); }, []);
  const unlockUser = async (email) => {
    try { await api.post("/admin/unlock-login", { email }); toast.success("Login unlocked"); loadLocked(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    try { await api.post("/amden-users", form); toast.success("Amden user created"); setDialog(false); setForm({ name: "", email: "", password: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (u) => {
    try { await api.put(`/amden-users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout nav={amdenNav} title="Amden Users">
      <PageHeader title="Amden Super Users" subtitle="Manage the Amden team's platform accounts">
        <Button data-testid="new-amden-user" onClick={() => setDialog(true)} className="bg-slate-900 text-white">+ New Amden user</Button>
      </PageHeader>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200"><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Last login</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} data-testid={`amden-user-${u.id}`} className="border-b border-zinc-50">
                <td className="p-3 font-medium">{u.name}{u.id === user?.id && <span className="ml-2 text-[10px] uppercase text-amber-600 font-bold">You</span>}</td>
                <td className="p-3 text-zinc-500">{u.email}</td>
                <td className="p-3 num text-xs">{u.last_login ? fmtDateTime(u.last_login) : "—"}</td>
                <td className="p-3">{u.active ? <span className="text-emerald-600 text-xs font-bold">Active</span> : <span className="text-zinc-400 text-xs font-bold">Disabled</span>}{lockedMap[u.email] != null && <span className="ml-1 text-[10px] font-bold uppercase bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded" data-testid={`amden-locked-${u.id}`}>Locked</span>}</td>
                <td className="p-3 text-right"><div className="flex gap-1 justify-end">{lockedMap[u.email] != null && <Button size="sm" variant="outline" className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`amden-unlock-${u.id}`} onClick={() => unlockUser(u.email)}><Lock className="h-3.5 w-3.5 mr-1" />Unlock</Button>}{u.id !== user?.id && <Button size="sm" variant="outline" className="h-8" data-testid={`amden-toggle-${u.id}`} onClick={() => toggle(u)}>{u.active ? "Disable" : "Enable"}</Button>}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Amden user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="au-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Email</Label><Input data-testid="au-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Password</Label><Input data-testid="au-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
            <Button data-testid="au-submit" onClick={create} className="w-full bg-slate-900 text-white">Create user</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
